/**
 * @module DOMPatch
 * @description DOM diffing and patching engine. Morphs existing DOM nodes to match new
 * HTML without destroying state (focus, scroll position, custom-element instances).
 * Supports skip/preserve hooks and resource-attribute deduplication.
 * @requires @fc/component
 */
import { FCElement } from '@fc/component';

const RESOURCE_ATTRS = new Set(['src', 'href', 'srcset', 'poster']);
const OPEN_DIALOG_RUNTIME_CLASSES = [
  'fit-header-surface-dialog--attached',
  'fit-header-attached-surface',
  'fit-header-surface-placement--interactive',
  'dialog-closing',
];

function preserveRuntimeClasses(prev, next, classNames = []) {
  if (!(prev instanceof Element) || !(next instanceof Element)) return;

  for (const className of classNames) {
    next.classList.toggle(className, prev.classList.contains(className));
  }
}

function preserveOpenDialogRuntimeState(prev, next) {
  if (!(prev instanceof HTMLDialogElement) || !(next instanceof HTMLDialogElement) || !prev.open) return;

  preserveRuntimeClasses(prev, next, OPEN_DIALOG_RUNTIME_CLASSES);

  if (prev.hasAttribute('aria-modal')) {
    next.setAttribute('aria-modal', prev.getAttribute('aria-modal') ?? '');
  } else {
    next.removeAttribute('aria-modal');
  }
}

const PATCH_DEFAULTS = {
  childrenOnly: true,

  shouldSkip(prev, next) {
    if (next.nodeType === Node.TEXT_NODE && next.nodeValue?.trim() === '') return true;

    if (
      next instanceof HTMLTemplateElement &&
      next.shadowRootMode === 'open' &&
      prev.parentElement &&
      next.parentElement &&
      prev.parentElement.tagName === next.parentElement.tagName &&
      // eslint-disable-next-line eqeqeq
      prev.parentElement?.shadowRoot != null
    ) {
      return true;
    }

    if (next.nodeType === Node.COMMENT_NODE && next.nodeValue === 'shopify:rendered_by_section_api') return true;

    return false;
  },

  beforePatch(prev, next) {
    if (!(prev instanceof Element) || !(next instanceof Element)) return;

    if (prev instanceof FCElement && typeof prev.beforeMorphSync === 'function') {
      prev.beforeMorphSync(next);
    }

    // Section morphs can refresh header-owned cart/search/account surfaces while
    // their dialog stays open. Preserve runtime-owned dialog classes/semantics
    // so attached Interactive Island surfaces do not fall back to server markup
    // until the shopper closes and reopens them.
    preserveOpenDialogRuntimeState(prev, next);

    // Preserve attrs that should persist across morphs
    for (const name of ['product-grid-view']) {
      const pv = prev.getAttribute(name);
      if (pv && pv !== next.getAttribute(name)) next.setAttribute(name, pv);
    }

    // Preserve inline styles for specific tags and ids
    for (const tag of ['FLOAT-PANEL']) {
      if (prev.tagName === tag && next.tagName === tag) {
        const s = prev.getAttribute('style');
        if (s) next.setAttribute('style', s);
      }
    }
    for (const id of ['account-desktop-drawer']) {
      if (prev.id === id && next.id === id) {
        const s = prev.getAttribute('style');
        if (s) next.setAttribute('style', s);
      }
    }

    // Preserve view transition names
    if (prev instanceof HTMLElement && next instanceof HTMLElement && prev.style.viewTransitionName) {
      next.style.viewTransitionName = prev.style.viewTransitionName;
    }
  },

  afterPatch(node) {
    if (node instanceof FCElement) queueMicrotask(() => node.onMorphSync());
  },
};

Object.freeze?.(PATCH_DEFAULTS);

// --- Helpers (bottom-up) ---

const nodeKey = (node, opts) => opts?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);

const isSame = (a, b, opts) => {
  if (a.nodeType !== b.nodeType) return false;

  if (a.nodeType === Node.ELEMENT_NODE) {
    if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;
    const ak = nodeKey(a, opts);
    const bk = nodeKey(b, opts);
    if (ak && bk && ak !== bk) return false;
  }

  if (a.nodeType === Node.TEXT_NODE) return a.nodeValue?.trim() === b.nodeValue?.trim();
  if (a.nodeType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;

  return true;
};

const syncAttr = (next, prev, name) => {
  if (next[name] !== prev[name]) {
    prev[name] = next[name];
    // eslint-disable-next-line eqeqeq
    next[name] != null ? prev.setAttribute(name, '') : prev.removeAttribute(name);
  }
};

function preservesQuantityEdit(next, prev) {
  return prev instanceof HTMLInputElement && next instanceof HTMLInputElement &&
    document.activeElement === prev && prev.hasAttribute('data-quantity-editing') &&
    Boolean(prev.closest('qty-selector')) && !next.disabled && !next.readOnly &&
    Boolean(prev.dataset.cartKey) && prev.dataset.cartKey === next.dataset.cartKey;
}

function syncAttrs(next, prev, preserveQuantityEdit = false) {
  for (const { name, namespaceURI: ns, value: val, localName: local } of [...next.attributes]) {
    if (preserveQuantityEdit && (name === 'value' || name === 'data-quantity-editing')) continue;
    if (RESOURCE_ATTRS.has(name) && prev.getAttribute(name) === val) continue;

    if (ns) {
      if (prev.getAttributeNS(ns, local ?? name) !== val) prev.setAttributeNS(ns, local ?? name, val);
    } else {
      const existing = prev.getAttribute(name);
      if (existing === val) continue;
      val === 'null' || val === 'undefined' ? prev.removeAttribute(name) : prev.setAttribute(name, val);
    }
  }

  for (const { name, namespaceURI: ns, localName: local } of [...prev.attributes]) {
    if (preserveQuantityEdit && (name === 'value' || name === 'data-quantity-editing')) continue;
    ns
      ? !next.hasAttributeNS(ns, local ?? name) && prev.removeAttributeNS(ns, local ?? name)
      : !next.hasAttribute(name) && prev.removeAttribute(name);
  }
}

function syncInput(next, prev, preserveQuantityEdit = false) {
  const val = next.value;
  syncAttr(next, prev, 'checked');
  syncAttr(next, prev, 'disabled');
  if (next.indeterminate !== prev.indeterminate) prev.indeterminate = next.indeterminate;
  if (prev.type === 'file' || preserveQuantityEdit) return;

  if (val !== prev.value) {
    prev.setAttribute('value', val);
    prev.value = val;
  }
  if (val === 'null') {
    prev.value = '';
    prev.removeAttribute('value');
  }
  if (!next.hasAttributeNS(null, 'value')) {
    prev.removeAttribute('value');
  } else if (prev.type === 'range') {
    prev.value = val;
  }
}

function syncTextarea(next, prev) {
  const val = next.value;
  if (val !== prev.value) prev.value = val;

  const kid = prev.firstChild;
  if (kid?.nodeType !== Node.TEXT_NODE) return;
  if (val === '' && kid.nodeValue === prev.placeholder) return;
  kid.nodeValue = val;
}

function syncNode(next, prev, opts) {
  opts.beforePatch?.(prev, next);
  // Only an unfinished edit of the same cart line owns its live value. Other
  // attributes (including new stock limits) always come from the server.
  const preserveQuantityEdit = preservesQuantityEdit(next, prev);

  // Preserve open state for details/dialog
  if (
    (next instanceof HTMLDetailsElement && prev instanceof HTMLDetailsElement) ||
    (next instanceof HTMLDialogElement && prev instanceof HTMLDialogElement)
  ) {
    if (!next.hasAttribute('declarative-open')) next.open = prev.open;
  }

  // Preserve slot and sizes
  if (prev instanceof HTMLElement && next instanceof HTMLElement) {
    for (const attr of ['slot', 'sizes']) {
      const pv = prev.getAttribute(attr);
      const nv = next.getAttribute(attr);
      if (pv !== nv) {
        // eslint-disable-next-line eqeqeq
        pv == null ? next.removeAttribute(attr) : next.setAttribute(attr, pv);
      }
    }
  }

  if (next instanceof Element && prev instanceof Element) {
    if (!prev.isEqualNode(next)) syncAttrs(next, prev, preserveQuantityEdit);
  } else if (next instanceof Text || next instanceof Comment) {
    if (prev.nodeValue !== next.nodeValue) prev.nodeValue = next.nodeValue;
  }

  if (next instanceof HTMLInputElement && prev instanceof HTMLInputElement) {
    syncInput(next, prev, preserveQuantityEdit);
  } else if (next instanceof HTMLOptionElement && prev instanceof HTMLOptionElement) {
    syncAttr(next, prev, 'selected');
  } else if (next instanceof HTMLTextAreaElement && prev instanceof HTMLTextAreaElement) {
    syncTextarea(next, prev);
  }
}

function syncKids(next, prev, opts) {
  if (
    prev instanceof Element &&
    prev.hasAttribute('data-skip-subtree-update') &&
    next instanceof Element &&
    next.hasAttribute('data-skip-subtree-update')
  ) {
    return;
  }

  let offset = 0;
  const limit = (prev.childNodes?.length ?? 0) + (next.childNodes?.length ?? 0) + 100;
  let count = 0;

  for (let i = 0; ; i++) {
    if (++count > limit) break;

    const prevKid = prev.childNodes?.[i];
    const nextKid = next.childNodes?.[i - offset];

    if (!prevKid && !nextKid) break;

    if (!nextKid) {
      prevKid?.parentNode === prev && prev.removeChild(prevKid);
      i--;
      continue;
    }

    if (!prevKid) {
      prev.appendChild(nextKid);
      offset++;
      continue;
    }

    if (isSame(nextKid, prevKid, opts)) {
      const result = traverse(nextKid, prevKid, opts);
      if (result !== prevKid && result.parentNode !== prev) {
        prev.replaceChild(result, prevKid);
        offset++;
      }
      continue;
    }

    if (opts.shouldSkip?.(prevKid, nextKid)) {
      nextKid.parentNode === next && next.removeChild(nextKid);
      i--;
      continue;
    }

    let match = null;
    for (let j = i; j < (prev.childNodes?.length ?? 0); j++) {
      const candidate = prev.childNodes[j];
      if (candidate && isSame(candidate, nextKid, opts)) {
        match = candidate;
        break;
      }
    }

    if (match) {
      const result = traverse(nextKid, match, opts);
      if (result !== match && result.parentNode !== prev) offset++;
      prevKid.parentNode === prev && prev.insertBefore(result, prevKid);
    } else if (!nodeKey(nextKid, opts) && !nodeKey(prevKid, opts)) {
      const result = traverse(nextKid, prevKid, opts);
      if (result !== prevKid && result.parentNode !== prev && prevKid.parentNode === prev) {
        prev.replaceChild(result, prevKid);
        offset++;
      }
    } else {
      prevKid.parentNode === prev && prev.insertBefore(nextKid, prevKid);
      offset++;
    }
  }
}

function traverse(next, prev, opts) {
  if (!prev) return next;
  if (!next) return prev;
  if (next.isSameNode?.(prev)) return prev;
  if (next.nodeType !== prev.nodeType) return next;

  if (next instanceof Element && prev instanceof Element) {
    if (prev.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return prev;
    if (next.tagName !== prev.tagName) return next;

    const nk = nodeKey(next, opts);
    const pk = nodeKey(prev, opts);
    if (nk && pk && nk !== pk) return next;
  }

  const skipNode =
    prev instanceof Element &&
    prev.hasAttribute('data-skip-node-update') &&
    next instanceof Element &&
    next.hasAttribute('data-skip-node-update');
  if (!skipNode) syncNode(next, prev, opts);
  syncKids(next, prev, opts);

  opts.afterPatch?.(next);
  return prev;
}

// --- Public API ---

export function patchDOM(prevTree, nextTree, opts = PATCH_DEFAULTS) {
  try {
    if (!prevTree || !nextTree) throw new Error('Both oldTree and newTree must be provided');

    if (typeof nextTree === 'string') {
      const parsed = new DOMParser().parseFromString(nextTree, 'text/html').body?.firstElementChild;
      if (!parsed) throw new Error('newTree string is not valid HTML');
      nextTree = parsed;
    }

    if (!(nextTree instanceof Node)) throw new Error('newTree must be a Node or a valid HTML string');

    const merged = opts === PATCH_DEFAULTS ? PATCH_DEFAULTS : { ...PATCH_DEFAULTS, ...opts };

    if (merged.childrenOnly) {
      syncKids(nextTree, prevTree, merged);
      return prevTree;
    }

    if (nextTree.nodeType === 11) throw new Error('newTree should have one root node (not a DocumentFragment)');

    return traverse(nextTree, prevTree, merged);
  } catch (err) {
    console.warn('morph: Failed to morph DOM tree', err); // eslint-disable-line no-console
    return prevTree;
  }
}
